// Sheet!Utilization
import { get, zipWith } from 'lodash';
import { createSelector } from 'reselect'

import { getNumProjectionYears } from './../../selectors';
import {
  IAutorickshawSplit,
  ICarSplit,
  IOwnershipMode,
  IPublicTransitSplit,
  ITwoWheelSplit,
  VehicleType,
} from './../../types/city';

import { calculateCumulativeAnnualGrowthRate } from './../../../services/statistics';
import { calculateAnnualDistance } from './utils';


// Constants from this page / Top of Spreadsheet
// Professor Sanjay Singh's (India Institute of Technology) 2006 publication
// "The demand for road-based passenger mobility in India: 1950-2030 and relevance for developing and developed countries"
// http://www.ejtir.tbm.tudelft.nl/issues/2006_03/pdf/2006_03_03.pdf
// Units: kms/year
const ANNUAL_PRIVATE_USAGE = {
  car: 12600,
  twoWheel: 6300,
}
const ANNUAL_BUS_USAGE = 114209;

// Utilization = km/ vehicle / annum
// Calculate
// base amount

// Currently returns values in KM
// Used by cars, autorickshaw, 2 wheeler

// Utilization in 2015, per Utilization B43:B49
const getBaseUtilization = (): IOwnershipMode => {

  const results = {
    autoRickshaw: {
      commercial: calculateAnnualDistance(16, 0, 5.7),
    },
    car: {
      commercial: calculateAnnualDistance(16, 0, 12),
      private: ANNUAL_PRIVATE_USAGE.car,
      public: 0, // cars not included
    },
    publicTransit: {
      bus: ANNUAL_BUS_USAGE,
      metro: ANNUAL_BUS_USAGE,
    },
    twoWheel: {
      commercial: calculateAnnualDistance(16, 0, 5.7),
      private: ANNUAL_PRIVATE_USAGE.twoWheel,
    },
  }

  return results;
}

const getUtilizationGrowth = (): IOwnershipMode => {
  const base = getBaseUtilization();
  const duration = 2030 - 2016; // growth rates are based on 2016 to 2030

  const results = {
    autoRickshaw: {
      commercial: calculateCumulativeAnnualGrowthRate(base.autoRickshaw.commercial, 33500, duration), // hardcoded
    },
    car: {
      commercial: calculateCumulativeAnnualGrowthRate(base.car.commercial, 124800, duration), // hardcoded
      private: 0,
      public: 0, // cars not included
    },
    publicTransit: {
      bus: 0,
      metro: 0,
    },
    twoWheel: {
      commercial: 0,
      private: 0,
    },
  }

  return results;
}


export const compoundGrowthSeries = (base: number,
  growthRate: number,
  numCycles: number): number[] => {
  const results = [base];

  // todo: rewrite this declaratively
  let runningTotal = base;
  for (let i = 0; i < numCycles; i++) {
    runningTotal *= growthRate;
    results.push(runningTotal);
  }

  return results;
}


const getUtilizationChunks = (paths: string[][], baseValue: IOwnershipMode, numYears: number, growth: IOwnershipMode) => {
  const groups = paths.map((path: string[]) => compoundGrowthSeries(get(baseValue, path),
    1 + get(growth, path),
    numYears)
  );

  // Because of how the spreadsheet works in cloning 2016 based on 2015,
  // we will chop off the last projection, and duplicate the first item
  // Non-pure: clone first value, and remove the last value.
  groups.forEach(group => {
    group.unshift(group[0]);
    group.pop();
  })
  return groups;
}

// TODO: utilize the similarity between this and getModalSplit to refactor
export const getUtilizationSeries = createSelector(
  [getNumProjectionYears],
  (numYears): IOwnershipMode[] => {

    const baseUtilization = getBaseUtilization();
    const growthMap = getUtilizationGrowth();

    // Initially this won't be DRY at all... refactor this another time since each item had diff keys
    // Tuples will be "keyname" and "pathname"
    const carPaths = [
      [VehicleType.car, 'commercial'],
      [VehicleType.car, 'private'],
      [VehicleType.car, 'public'],
    ];

    const carChunks = getUtilizationChunks(carPaths, baseUtilization, numYears, growthMap);
    const carSeries: ICarSplit[] = zipWith(...carChunks, (commercial, pri, pub) => {
      return {
        'commercial': commercial,
        'private': pri,
        'public': pub,
      }
    });


    const rickshawPaths = [
      ['autoRickshaw', 'commercial'],
    ]
    const rickshawChunks = getUtilizationChunks(rickshawPaths, baseUtilization, numYears, growthMap);
    const rickshawSeries: IAutorickshawSplit[] = zipWith(...rickshawChunks, (commercial) => ({
      'commercial': commercial,
    }));

    const twoWheelPaths = [
      [VehicleType.twoWheel, 'commercial'],
      [VehicleType.twoWheel, 'private'],
    ]
    const twoWheelChunks = getUtilizationChunks(twoWheelPaths, baseUtilization, numYears, growthMap);
    const twoWheelSeries: ITwoWheelSplit[] = zipWith(...twoWheelChunks, (commercial, pri) => {
      return {
        'commercial': commercial,
        'private': pri,
      }
    });

    const publicTransitPaths = [
      [VehicleType.publicTransit, 'bus'],
      [VehicleType.publicTransit, 'metro'],
    ]
    const publicTransitChunks = getUtilizationChunks(publicTransitPaths, baseUtilization, numYears, growthMap);
    const publicTransitSeries: IPublicTransitSplit[] = zipWith(...publicTransitChunks, (bus, metro) => {
      return {
        'bus': bus,
        'metro': metro,
      }
    });

    const combined: IOwnershipMode[] = zipWith(
      carSeries,
      rickshawSeries,
      twoWheelSeries,
      publicTransitSeries,
      (car: ICarSplit, autoRickshaw: IAutorickshawSplit,
        twoWheel: ITwoWheelSplit, publicTransit: IPublicTransitSplit) => ({
          autoRickshaw,
          car,
          publicTransit,
          twoWheel,
        }
        ));

    return combined;
  }
)

// For per-fuel-type specific data, see vehicleStock/byFuel



// WEBPACK FOOTER //
// ./src/store/selectors/utilization/index.ts